import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  FaArrowLeft,
  FaEdit,
  FaChartBar,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaTicketAlt,
  FaUser,
  FaImages,
  FaHandshake,
  FaFileAlt,
  FaPercent,
} from "react-icons/fa";
import { get } from "../../../lib/http";
import { API } from "../../../config/api";
import { AD } from "../../adminUi.js";

const cn = (...a) => a.filter(Boolean).join(" ");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n) => String(n).padStart(2, "0");

/** Same wall-clock as admin save / ticket PDF (UTC face). */
function wallDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return `${pad(d.getUTCDate())} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
function wallTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  let h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${pad(m)} ${ampm}`;
}
function wallDateTime(v) {
  if (!v) return "—";
  return `${wallDate(v)} · ${wallTime(v)}`;
}
function fmtDuration(m) {
  if (!m) return "—";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h ? `${h}h ${mm}m` : `${mm}m`;
}
function inr(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN")}`;
}
function imgFallback(e, url) {
  e.target.src = url;
}
function pickPrimaryShowtime(event) {
  const list = (event?.showtimes || []).filter((s) => s?.startTime);
  if (!list.length) return event?.date || null;
  list.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  return list[0].startTime;
}
function stripHtml(html) {
  if (!html || typeof html !== "string") return "";
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}
function isHtml(str) {
  return /<[a-z][\s\S]*>/i.test(String(str || ""));
}

function Badge({ tone = "gray", children }) {
  const map = {
    green: "bg-emerald-100 text-emerald-800 ring-emerald-200/60",
    red: "bg-rose-100 text-rose-800 ring-rose-200/60",
    amber: "bg-amber-100 text-amber-900 ring-amber-200/60",
    blue: "bg-sky-100 text-sky-800 ring-sky-200/60",
    gray: "bg-slate-100 text-slate-700 ring-slate-200/60",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset",
        map[tone] || map.gray
      )}
    >
      {children}
    </span>
  );
}

function Card({ title, icon: Icon, subtitle, children, className }) {
  return (
    <section
      className={cn(
        "overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900",
        className
      )}
    >
      {(title || subtitle) && (
        <header className="flex items-center gap-2.5 border-b border-slate-100 px-5 py-4 dark:border-gray-800">
          {Icon && (
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Icon className="text-sm" />
            </span>
          )}
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900 dark:text-white">{title}</h2>
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
          </div>
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100 break-words">
        {children ?? "—"}
      </dd>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <div className={cn(AD.statCard, "flex gap-3")}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm">
        <Icon className="text-sm" />
      </span>
      <div className="min-w-0">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className="truncate text-lg font-bold text-slate-900 dark:text-white">{value}</p>
        {sub && <p className="text-xs text-slate-500">{sub}</p>}
      </div>
    </div>
  );
}

function ProgressBar({ value, max }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (Number(value) / Number(max)) * 100)) : 0;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-gray-800">
      <div
        className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function ViewSkeleton() {
  return (
    <div className={cn(AD.formWrapWide, AD.page, "animate-pulse")}>
      <div className="h-8 w-48 rounded-lg bg-slate-200" />
      <div className="h-56 rounded-2xl bg-slate-200" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 rounded-2xl bg-slate-200" />
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="h-64 rounded-2xl bg-slate-200" />
        <div className="h-64 rounded-2xl bg-slate-200" />
      </div>
    </div>
  );
}

export default function AdminEventView() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const res = await get(`${API.events.getSinglEvent}/${id}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });
        if (alive) setEvent(res?.event || null);
      } catch (e) {
        if (alive) setErr(e?.message || "Failed to load event details");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  const primaryWhen = useMemo(() => pickPrimaryShowtime(event), [event]);

  const ticketStats = useMemo(() => {
    const cats = event?.ticketCategories || [];
    const total = cats.reduce((s, c) => s + Number(c.totalSeats || 0), 0);
    const avail = cats.reduce((s, c) => s + Number(c.availableSeats || 0), 0);
    return { total, avail, sold: Math.max(0, total - avail) };
  }, [event]);

  const statusTone = event?.isActived ? "green" : "red";
  const liveTone =
    event?.status === "Live"
      ? "green"
      : event?.status === "Cancelled"
        ? "red"
        : "amber";

  const gallery = useMemo(
    () => (event?.landsacpImage || []).filter(Boolean),
    [event]
  );

  const hasFees =
    event?.gstRate != null ||
    event?.convenienceFeeFlat != null ||
    event?.convenienceFeePercent != null;

  if (loading) return <ViewSkeleton />;

  if (err) {
    return (
      <div className={cn(AD.formWrapWide, AD.page)}>
        <Card title="Could not load event">
          <p className="text-sm text-rose-600">{err}</p>
          <Link to="/admin/events" className={cn(AD.btnPrimary, "mt-4 inline-flex no-underline")}>
            <FaArrowLeft /> Back to events
          </Link>
        </Card>
      </div>
    );
  }

  if (!event) {
    return (
      <div className={cn(AD.formWrapWide, AD.page)}>
        <Card title="Event not found">
          <Link to="/admin/events" className={cn(AD.btnPrimary, "inline-flex no-underline")}>
            <FaArrowLeft /> Back to events
          </Link>
        </Card>
      </div>
    );
  }

  const venueLine = [event.venue?.city, event.venue?.state].filter(Boolean).join(", ");

  return (
    <div className={cn(AD.formWrapWide, AD.page)}>
      {/* Top bar */}
      <div className={AD.headerRow}>
        <div>
          <nav className="mb-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
            <Link to="/admin/events" className="hover:text-amber-700">
              Events
            </Link>
            <span>/</span>
            <span className="truncate font-medium text-slate-700 dark:text-slate-300">
              {event.name || "Event"}
            </span>
          </nav>
          <h1 className={AD.title}>{event.name || "Event details"}</h1>
          <p className={AD.subtitle}>
            {event.category || "Uncategorized"}
            {venueLine ? ` · ${venueLine}` : ""}
          </p>
        </div>
        <div className={AD.toolbar}>
          <Link to="/admin/events" className={cn(AD.btnSecondary, "no-underline")}>
            <FaArrowLeft className="opacity-70" /> Back
          </Link>
          <Link
            to={`/admin/events/dashboard/${id}`}
            className={cn(AD.btnSecondary, "no-underline")}
          >
            <FaChartBar className="opacity-70" /> Dashboard
          </Link>
          <Link to={`/admin/events/edit/${id}`} className={cn(AD.btnPrimary, "no-underline")}>
            <FaEdit /> Edit event
          </Link>
        </div>
      </div>

      {/* Banner */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm dark:border-gray-800">
        <div className="relative aspect-[21/9] min-h-[180px] max-h-[320px] w-full bg-slate-100">
          <img
            src={event.imageUrl || "https://via.placeholder.com/1600x500?text=Event+Banner"}
            alt={event.name || "Event banner"}
            className="h-full w-full object-cover"
            onError={(e) => imgFallback(e, "https://via.placeholder.com/1600x500?text=Event+Banner")}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
          <div className="absolute bottom-4 left-4 right-4 flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              <Badge tone={liveTone}>{event.status || "Upcoming"}</Badge>
              <Badge tone={statusTone}>{event.isActived ? "Active" : "Inactive"}</Badge>
              {event.isVerified && <Badge tone="blue">Verified</Badge>}
              {event.isBookingOpen && <Badge tone="green">Booking open</Badge>}
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          icon={FaCalendarAlt}
          label="Show date"
          value={wallDate(primaryWhen)}
          sub={primaryWhen ? new Date(primaryWhen).toLocaleDateString("en-GB", { weekday: "long", timeZone: "UTC" }) : ""}
        />
        <Kpi
          icon={FaClock}
          label="Show time"
          value={wallTime(primaryWhen)}
          sub={event.duration ? `Duration ${fmtDuration(event.duration)}` : "Onwards"}
        />
        <Kpi
          icon={FaMapMarkerAlt}
          label="Venue"
          value={event.venue?.name || "—"}
          sub={venueLine || event.venue?.address || ""}
        />
        <Kpi
          icon={FaTicketAlt}
          label="Tickets"
          value={`${ticketStats.sold.toLocaleString()} sold`}
          sub={
            ticketStats.total
              ? `${ticketStats.avail.toLocaleString()} of ${ticketStats.total.toLocaleString()} left`
              : "No capacity set"
          }
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main column */}
        <div className="space-y-6 lg:col-span-2">
          <Card title="Event details" icon={FaFileAlt} subtitle="Core information">
            <dl className="grid gap-5 sm:grid-cols-2">
              <Field label="Event name">{event.name}</Field>
              <Field label="Category">{event.category}</Field>
              <Field label="Language">{event.language || "—"}</Field>
              <Field label="Age restriction">
                {event.ageRestriction ? `${event.ageRestriction}+` : "—"}
              </Field>
              <Field label="Event date (admin)">{wallDateTime(event.date)}</Field>
              <Field label="Verified">{event.isVerified ? "Yes" : "No"}</Field>
            </dl>

            {(event.description || stripHtml(event.description)) && (
              <div className="mt-6 rounded-xl border border-amber-100/80 bg-amber-50/40 p-4 dark:border-amber-900/30 dark:bg-amber-950/20">
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-amber-800/80">
                  Description
                </p>
                {isHtml(event.description) ? (
                  <div
                    className="prose prose-sm max-w-none text-slate-800 dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: event.description }}
                  />
                ) : (
                  <p className="text-sm leading-relaxed text-slate-700">{event.description}</p>
                )}
              </div>
            )}

            {event.remark && (
              <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50/80 p-4 text-sm text-slate-700">
                <span className="font-semibold text-slate-500">Remark: </span>
                {event.remark}
              </div>
            )}
          </Card>

          {Array.isArray(event.ticketCategories) && event.ticketCategories.length > 0 && (
            <Card title="Ticket categories" icon={FaTicketAlt} subtitle="Pricing and availability">
              <div className={AD.tableShell}>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[520px] text-left text-sm">
                    <thead className={AD.thead}>
                      <tr>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3">Price</th>
                        <th className="px-4 py-3">Seats</th>
                        <th className="px-4 py-3">Available</th>
                        <th className="px-4 py-3">Fill</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                      {event.ticketCategories.map((t, i) => {
                        const total = Number(t.totalSeats || 0);
                        const avail = Number(t.availableSeats || 0);
                        const sold = Math.max(0, total - avail);
                        return (
                          <tr key={i} className="hover:bg-amber-50/30 dark:hover:bg-gray-800/50">
                            <td className="px-4 py-3 font-medium">{t.type || "—"}</td>
                            <td className="px-4 py-3">{t.price != null ? inr(t.price) : "—"}</td>
                            <td className="px-4 py-3">{total ? total.toLocaleString() : "—"}</td>
                            <td className="px-4 py-3">{avail.toLocaleString()}</td>
                            <td className="px-4 py-3 min-w-[120px]">
                              <ProgressBar value={sold} max={total || 1} />
                              <span className="mt-1 block text-[11px] text-slate-500">
                                {sold.toLocaleString()} sold
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>
          )}

          {Array.isArray(event.showtimes) && event.showtimes.length > 0 && (
            <Card title="Showtimes" icon={FaClock} subtitle="Schedules configured in admin">
              <div className={AD.tableShell}>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[480px] text-left text-sm">
                    <thead className={AD.thead}>
                      <tr>
                        <th className="px-4 py-3">Start</th>
                        <th className="px-4 py-3">End</th>
                        <th className="px-4 py-3">Seats</th>
                        <th className="px-4 py-3">Pass price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                      {event.showtimes.map((s, i) => (
                        <tr key={i} className="hover:bg-amber-50/30 dark:hover:bg-gray-800/50">
                          <td className="px-4 py-3 font-medium">{wallDateTime(s.startTime)}</td>
                          <td className="px-4 py-3">{wallDateTime(s.endTime)}</td>
                          <td className="px-4 py-3">
                            {s.availableSeats?.toLocaleString() ?? "—"}
                          </td>
                          <td className="px-4 py-3">
                            {s.ticketPrice != null ? inr(s.ticketPrice) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>
          )}

          <Card title="Venue" icon={FaMapMarkerAlt}>
            <div className="mb-4 flex items-center gap-3">
              {event.venue?.logoUrl && (
                <img
                  src={event.venue.logoUrl}
                  alt=""
                  className="h-12 w-12 rounded-xl border border-slate-100 object-contain"
                  onError={(e) => imgFallback(e, "https://via.placeholder.com/80?text=Venue")}
                />
              )}
              <div>
                <p className="font-bold text-slate-900">{event.venue?.name || "—"}</p>
                <p className="text-sm text-slate-500">{event.venue?.address || ""}</p>
              </div>
            </div>
            <dl className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
              <Field label="City">{event.venue?.city}</Field>
              <Field label="State">{event.venue?.state}</Field>
              <Field label="Country">{event.venue?.country}</Field>
              <Field label="Pincode">{event.venue?.pincode}</Field>
              <Field label="Capacity">
                {event.venue?.capacity ? Number(event.venue.capacity).toLocaleString() : "—"}
              </Field>
              <Field label="Coordinates">
                {event.venue?.latitude && event.venue?.longitude
                  ? `${event.venue.latitude}, ${event.venue.longitude}`
                  : "—"}
              </Field>
            </dl>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {gallery.length > 0 && (
            <Card title="Gallery" icon={FaImages} subtitle={`${gallery.length} image(s)`}>
              <div className="grid grid-cols-2 gap-2">
                {gallery.map((img, i) => (
                  <a
                    key={i}
                    href={img}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block overflow-hidden rounded-xl ring-1 ring-slate-200 transition hover:ring-amber-300"
                  >
                    <img
                      src={img}
                      alt={`Gallery ${i + 1}`}
                      className="aspect-[4/3] w-full object-cover transition group-hover:scale-105"
                      onError={(e) => imgFallback(e, "https://via.placeholder.com/400x300?text=Image")}
                    />
                  </a>
                ))}
              </div>
            </Card>
          )}

          <Card title="Organizer" icon={FaUser}>
            <dl className="space-y-4">
              <Field label="Name">{event.organizer?.name}</Field>
              <Field label="Email">
                {event.organizer?.contactEmail ? (
                  <a
                    href={`mailto:${event.organizer.contactEmail}`}
                    className="text-amber-700 hover:underline"
                  >
                    {event.organizer.contactEmail}
                  </a>
                ) : (
                  "—"
                )}
              </Field>
              <Field label="Phone">{event.organizer?.contactPhone}</Field>
              {event.organizer?.website && (
                <Field label="Website">
                  <a
                    href={event.organizer.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-amber-700 hover:underline"
                  >
                    {event.organizer.website}
                  </a>
                </Field>
              )}
            </dl>
            {event.organizer?.logoUrl && (
              <img
                src={event.organizer.logoUrl}
                alt="Organizer"
                className="mt-4 h-14 rounded-lg border border-slate-100 object-contain"
              />
            )}
          </Card>

          {Array.isArray(event.sponsors) && event.sponsors.some((s) => s?.name) && (
            <Card title="Sponsors" icon={FaHandshake}>
              <ul className="space-y-3">
                {event.sponsors
                  .filter((s) => s?.name)
                  .map((s, i) => (
                    <li
                      key={i}
                      className="flex gap-3 rounded-xl border border-slate-100 p-3 dark:border-gray-800"
                    >
                      {s.logo && (
                        <img
                          src={s.logo}
                          alt=""
                          className="h-10 w-10 shrink-0 rounded-lg object-contain"
                        />
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900">{s.name}</p>
                        <Badge tone="blue">{s.type || "Sponsor"}</Badge>
                        {s.description && (
                          <p className="mt-1 line-clamp-2 text-xs text-slate-500">{s.description}</p>
                        )}
                      </div>
                    </li>
                  ))}
              </ul>
            </Card>
          )}

          {hasFees && (
            <Card title="Fees & GST" icon={FaPercent}>
              <dl className="space-y-3">
                {event.gstRate != null && (
                  <Field label="GST rate">{Number(event.gstRate) * 100}%</Field>
                )}
                {event.conveniencesFeeType && (
                  <Field label="Convenience fee type">{event.conveniencesFeeType}</Field>
                )}
                {event.convenienceFeePercent != null && (
                  <Field label="Convenience %">{Number(event.convenienceFeePercent) * 100}%</Field>
                )}
                {event.convenienceFeeFlat != null && (
                  <Field label="Convenience flat">{inr(event.convenienceFeeFlat)}</Field>
                )}
              </dl>
            </Card>
          )}
        </div>
      </div>

      {Array.isArray(event.termsAndConditions) &&
        event.termsAndConditions.some((t) => t?.point?.trim()) && (
          <Card title="Terms & conditions" icon={FaFileAlt}>
            <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-slate-700">
              {event.termsAndConditions
                .filter((t) => t?.point?.trim())
                .map((t, i) => (
                  <li key={i}>{t.point}</li>
                ))}
            </ul>
          </Card>
        )}
    </div>
  );
}
